import { getActiveClient } from 'shared/store/clients/selectors'
import { emptySplitApi, RootState } from 'shared/store/store'
import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'
import {
  BblContactModel,
  ContactModelContactsQueryResult,
  PortalUsersQueryParams,
  UpdateContactModel,
} from './types'
import { ContactsQueryModel } from './types'
import {
  GET_COHABITANTS,
  GET_PORTAL_USERS,
  GET_PORTAL_SUMMARY,
} from 'shared/constants/storeTags'
import {
  IPortalUserResponseModel,
  IPortalUserSummary,
} from 'Modules/Communication/PortalUsers/shared/models/PortalUsers'

const baseUrl = 'client/clients'

const clientService = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    getContacts: build.query<
      ContactModelContactsQueryResult,
      ContactsQueryModel
    >({
      async queryFn(data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/${client.id}/contacts/query`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
          data,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av kontakter',
            }),
          }
        }

        return { data: result.data as ContactModelContactsQueryResult }
      },
    }),
    getBBlContacts: build.query<BblContactModel, void>({
      async queryFn(data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/${client.id}/bblcontacts`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved henting av kontakter',
            }),
          }
        }

        return { data: result.data as BblContactModel }
      },
    }),
    getHousingRules: build.query<any, void>({
      async queryFn(data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/${client.id}/documents?isBoardPortal=true`,
          method: 'GET',
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av regler og vedtekter',
            }),
          }
        }

        return { data: result.data as any }
      },
    }),
    updateContact: build.mutation<void, UpdateContactModel>({
      invalidatesTags: [GET_COHABITANTS],
      async queryFn(data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/${client.id}/contacts/${data.nameId}`,
          method: 'PUT',
          data: data.values,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError: 'Det skjedde en feil ved oppdatering av kontakt',
            }),
          }
        }
        return { data: undefined }
      },
    }),
    getPortalUsers: build.query<
      IPortalUserResponseModel,
      PortalUsersQueryParams
    >({
      providesTags: [GET_PORTAL_USERS],
      async queryFn(data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/${client.id}/portalusers`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
          data,
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av digitale brukere',
            }),
          }
        }

        return { data: result.data as IPortalUserResponseModel }
      },
    }),
    getPortalUserSummary: build.query<IPortalUserSummary, void>({
      providesTags: [GET_PORTAL_SUMMARY],
      async queryFn(_data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url: baseUrl + `/${client.id}/portalusers/summary`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'Det skjedde en feil ved henting av portalbrukeroppsummering',
            }),
          }
        }

        return { data: result.data as IPortalUserSummary }
      },
    }),
    updateElectronicCommunication: build.mutation<
      void,
      { nameId: string; acceptElectronicCommunication: number }
    >({
      invalidatesTags: [GET_PORTAL_USERS, GET_PORTAL_SUMMARY],
      async queryFn(data, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const result = await query({
          url:
            baseUrl +
            `/${client.id}/contacts/${data.nameId}/electroniccommunication`,
          method: 'PUT',
          data: {
            acceptElectronicCommunication: data.acceptElectronicCommunication,
          },
        })

        if (result.error) {
          return {
            error: axiosErrorMessageHelper(result.error, {
              fallbackError:
                'En feil har oppstått ved oppdatering av reservasjon',
            }),
          }
        }
        return { data: undefined }
      },
    }),
  }),
})

export const {
  useGetContactsQuery,
  useGetBBlContactsQuery,
  useGetHousingRulesQuery,
  useUpdateContactMutation,
  useUpdateElectronicCommunicationMutation,
  useGetPortalUsersQuery,
  useGetPortalUserSummaryQuery,
} = clientService

export default clientService
